import Decimal from "decimal.js";
import { Commodities } from "@/lib/api/otc";
import { OtcFormValues } from "./types";

// ── Field name unions ───────────────────────────────────────────────

export type MarginWarningField =
  | "margin_amount"
  | "adj_margin_amount"
  | "adj_margin_amount_two"
  | "adj_margin_amount_three"
  | "futures_margin"
  | "futures_margin_step_one"
  | "futures_margin_step_two"
  | "futures_margin_double_up"
  | "final_futures_price_margin";

export type StepWarningField = "step_one" | "step_two" | "step_three" | "double_up";

const MARGIN_WARNING_FIELDS: ReadonlyArray<MarginWarningField> = [
  "margin_amount",
  "adj_margin_amount",
  "adj_margin_amount_two",
  "adj_margin_amount_three",
  "futures_margin",
  "futures_margin_step_one",
  "futures_margin_step_two",
  "futures_margin_double_up",
  "final_futures_price_margin",
];

const STEP_WARNING_FIELDS: ReadonlyArray<StepWarningField> = [
  "step_one",
  "step_two",
  "step_three",
  "double_up",
];

// ── Step bounds table ───────────────────────────────────────────────

export type StepWarningLevel = "outside_normal" | "outside_exaggerated";
export type StepWarningDirection = "below" | "above";

export interface StepBounds {
  normal: { min: Decimal; max: Decimal };
  exaggerated: { min: Decimal; max: Decimal };
}

type StepBoundCommodity =
  | Commodities.Corn
  | Commodities.Soybeans
  | Commodities.MinneapolisWheat
  | Commodities.WinterWheat
  | Commodities.WinterWheatPrelimCommoditySymbol
  | Commodities.Canola;

const STEP_BOUNDS: Record<StepBoundCommodity, StepBounds> = {
  [Commodities.Corn]: {
    normal: { min: new Decimal("2.90"), max: new Decimal("6.00") },
    exaggerated: { min: new Decimal("2.50"), max: new Decimal("8.50") },
  },
  [Commodities.Soybeans]: {
    normal: { min: new Decimal("8.00"), max: new Decimal("13.00") },
    exaggerated: { min: new Decimal("7.00"), max: new Decimal("18.00") },
  },
  [Commodities.MinneapolisWheat]: {
    normal: { min: new Decimal("5.00"), max: new Decimal("9.00") },
    exaggerated: { min: new Decimal("4.00"), max: new Decimal("13.00") },
  },
  [Commodities.WinterWheat]: {
    normal: { min: new Decimal("5.00"), max: new Decimal("9.00") },
    exaggerated: { min: new Decimal("4.00"), max: new Decimal("13.00") },
  },
  [Commodities.WinterWheatPrelimCommoditySymbol]: {
    normal: { min: new Decimal("5.00"), max: new Decimal("9.00") },
    exaggerated: { min: new Decimal("4.00"), max: new Decimal("13.00") },
  },
  [Commodities.Canola]: {
    normal: { min: new Decimal("400.00"), max: new Decimal("650.00") },
    exaggerated: { min: new Decimal("300.00"), max: new Decimal("1250.00") },
  },
};

const COMMODITY_LABEL: Record<StepBoundCommodity, string> = {
  [Commodities.Corn]: "Corn",
  [Commodities.Soybeans]: "Soybeans",
  [Commodities.MinneapolisWheat]: "Minneapolis Wheat",
  [Commodities.WinterWheat]: "Winter Wheat",
  [Commodities.WinterWheatPrelimCommoditySymbol]: "Winter Wheat",
  [Commodities.Canola]: "Canola",
};

function isStepBoundCommodity(value: string | undefined): value is StepBoundCommodity {
  if (!value) return false;
  return (
    value === Commodities.Corn ||
    value === Commodities.Soybeans ||
    value === Commodities.MinneapolisWheat ||
    value === Commodities.WinterWheat ||
    value === Commodities.WinterWheatPrelimCommoditySymbol ||
    value === Commodities.Canola
  );
}

// ── Warning-specific decimal parser ─────────────────────────────────

/**
 * Parse a form value as a Decimal for warning detection.
 *
 * Returns null for null/undefined/empty/whitespace/unparseable input.
 * Unlike `ignoreSafeParseDecimal`, this does NOT coerce invalid input to 0,
 * which would otherwise produce false warnings (e.g. for Corn step bounds
 * where 0 is "outside_exaggerated") or hide invalid values.
 *
 * Normalizes common form artifacts: commas, `$`, whitespace, and accounting
 * parentheses (e.g. `($1,500)` -> -1500).
 */
export function parseWarningDecimal(input: unknown): Decimal | null {
  if (input === null || input === undefined) return null;

  let raw: string;
  if (typeof input === "number") {
    if (!Number.isFinite(input)) return null;
    raw = String(input);
  } else if (typeof input === "string") {
    raw = input;
  } else {
    return null;
  }

  const trimmed = raw.trim();
  if (trimmed === "") return null;

  // Accounting parentheses -> negative
  let negativeFromParens = false;
  let body = trimmed;
  if (body.startsWith("(") && body.endsWith(")")) {
    negativeFromParens = true;
    body = body.slice(1, -1).trim();
  }

  // Strip $ , and internal whitespace
  const cleaned = body.replace(/[$,\s]/g, "");
  if (cleaned === "" || cleaned === "-" || cleaned === "+") return null;

  try {
    const dec = new Decimal(cleaned);
    return negativeFromParens ? dec.neg() : dec;
  } catch {
    return null;
  }
}

// ── Warning result types ────────────────────────────────────────────

export interface MarginWarning {
  field: MarginWarningField;
  label: string;
  value: Decimal;
  threshold: Decimal;
}

export interface StepWarning {
  field: StepWarningField;
  label: string;
  value: Decimal;
  commodity: StepBoundCommodity;
  commodityLabel: string;
  bounds: StepBounds;
  level: StepWarningLevel;
  direction: StepWarningDirection;
}

export interface OtcFormWarnings {
  margin: MarginWarning[];
  step: StepWarning[];
  hasAny: boolean;
}

const MARGIN_THRESHOLD = new Decimal("1500");

// ── Public API ──────────────────────────────────────────────────────

export interface ComputeOtcFormWarningsArgs {
  values: OtcFormValues;
  /** Field-meta/schema visibility: `form.getFieldProps(name)?.visible`. */
  isFieldVisible: (name: string) => boolean;
  /**
   * Whether the margin field's row is actually rendered in the DOM.
   * For base/canola margins this should mirror `isFieldVisible`. For
   * adjusted margin fields it must also account for the local
   * collapsed-rows state in OtcContractForm.
   */
  isMarginFieldRendered: (name: MarginWarningField) => boolean;
  /** Resolve a field's label, typically `form.getFieldProps(name)?.label ?? name`. */
  getFieldLabel: (name: string) => string;
}

export function computeOtcFormWarnings(args: ComputeOtcFormWarningsArgs): OtcFormWarnings {
  const { values, isFieldVisible, isMarginFieldRendered, getFieldLabel } = args;

  const margin: MarginWarning[] = [];
  for (const field of MARGIN_WARNING_FIELDS) {
    if (!isMarginFieldRendered(field)) continue;
    const raw = (values as Record<string, unknown>)[field];
    const value = parseWarningDecimal(raw);
    if (value === null) continue;
    if (!value.abs().gte(MARGIN_THRESHOLD)) continue;
    margin.push({
      field,
      label: getFieldLabel(field),
      value,
      threshold: MARGIN_THRESHOLD,
    });
  }

  const step: StepWarning[] = [];
  const commodityRaw = typeof values.commodity === "string" ? values.commodity : undefined;
  if (isStepBoundCommodity(commodityRaw)) {
    const bounds = STEP_BOUNDS[commodityRaw];
    const commodityLabel = COMMODITY_LABEL[commodityRaw];
    for (const field of STEP_WARNING_FIELDS) {
      if (!isFieldVisible(field)) continue;
      const raw = (values as Record<string, unknown>)[field];
      const value = parseWarningDecimal(raw);
      if (value === null) continue;

      // Inside normal bounds (inclusive) -> no warning.
      if (bounds.normal.min.lte(value) && value.lte(bounds.normal.max)) continue;

      const below = value.lt(bounds.normal.min);
      const direction: StepWarningDirection = below ? "below" : "above";

      // Exaggerated bounds: at or beyond -> stronger warning.
      const atOrBeyondExaggerated = below
        ? value.lte(bounds.exaggerated.min)
        : value.gte(bounds.exaggerated.max);
      const level: StepWarningLevel = atOrBeyondExaggerated
        ? "outside_exaggerated"
        : "outside_normal";

      step.push({
        field,
        label: getFieldLabel(field),
        value,
        commodity: commodityRaw,
        commodityLabel,
        bounds,
        level,
        direction,
      });
    }
  }

  return {
    margin,
    step,
    hasAny: margin.length > 0 || step.length > 0,
  };
}
